import React from "react"
import { Box, Button, Tooltip } from "@mui/material"
import NoteAddIcon from "@mui/icons-material/NoteAdd"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import PrintIcon from "@mui/icons-material/Print"
import VisibilityIcon from "@mui/icons-material/Visibility"
import { useTranslation } from "react-i18next"

interface DocumentActionsProps {
    document: {
        id: string
        status: string
        document_type: string
    }
    onCreateCreditNote?: () => void
    onCreateInvoice?: () => void
    onDownloadPdf?: () => void
    onPrint?: () => void
    onPreview?: () => void
    loading?: boolean
    canCreateCreditNote?: boolean
}

const DocumentActions: React.FC<DocumentActionsProps> = ({
    document,
    onCreateCreditNote,
    onCreateInvoice,
    onDownloadPdf,
    onPrint,
    onPreview,
    loading = false,
    canCreateCreditNote = true,
}) => {
    const { t } = useTranslation()
    const { status, document_type } = document
    const isValidated = status === "validated"
    const isInvoice = document_type === "invoice"
    const isReceipt = document_type === "receipt"
    const isA4Document = [
        "invoice",
        "credit_note",
        "quotation",
        "sales_order",
        "proforma",
    ].includes(document_type)

    return (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {isValidated && isInvoice && onCreateCreditNote && (
                <Tooltip
                    title={
                        canCreateCreditNote
                            ? t("documentActions.tooltip.createCreditNote")
                            : t("documentActions.tooltip.maxCreditReached")
                    }
                >
                    <span>
                        <Button
                            variant="outlined"
                            size="small"
                            color="warning"
                            startIcon={<NoteAddIcon />}
                            onClick={onCreateCreditNote}
                            disabled={loading || !canCreateCreditNote}
                        >
                            {t("documentActions.creditNote")}
                        </Button>
                    </span>
                </Tooltip>
            )}

            {isValidated && isReceipt && onCreateInvoice && (
                <Tooltip title={t("documentActions.tooltip.createInvoice")}>
                    <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        startIcon={<ReceiptLongIcon />}
                        onClick={onCreateInvoice}
                        disabled={loading}
                    >
                        {t("documentActions.createInvoice")}
                    </Button>
                </Tooltip>
            )}

            {onPreview && (
                <Tooltip title={t("documentActions.tooltip.preview")}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={onPreview}
                        disabled={loading}
                    >
                        {t("documentActions.preview")}
                    </Button>
                </Tooltip>
            )}

            {isA4Document && onDownloadPdf && (
                <Tooltip title={t("documentActions.tooltip.downloadPdf")}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={onDownloadPdf}
                        disabled={loading}
                    >
                        PDF
                    </Button>
                </Tooltip>
            )}

            {(isA4Document || document_type === "receipt") && onPrint && (
                <Tooltip title={t("documentActions.tooltip.print")}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PrintIcon />}
                        onClick={onPrint}
                        disabled={loading}
                    >
                        {t("documentActions.print")}
                    </Button>
                </Tooltip>
            )}
        </Box>
    )
}

export default DocumentActions
